// AI copy quality check — flags marketing/awkward phrases per language and applies a plain, honest rewrite with one click.
import React, { useState } from 'react';
import { Loader2, ShieldCheck, Sparkles, Wand2, X } from 'lucide-react';
import { apiCall } from './_shared';

const TYPE_STYLE = {
  marketing: 'bg-rose-50 text-rose-700 border-rose-200',
  claim: 'bg-amber-50 text-amber-800 border-amber-200',
  awkward: 'bg-sky-50 text-sky-700 border-sky-200',
  grammar: 'bg-slate-100 text-slate-700 border-slate-200',
};

const scoreTone = (s) => (s >= 85 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : s >= 60 ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-rose-700 bg-rose-50 border-rose-200');

// Shared state machine: run(texts) → results; apply(key) marks the rewrite as used.
export const useCopyCheck = (context) => {
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (texts) => {
    const items = texts.filter((t) => (t.text || '').trim());
    if (!items.length) { setError('Nothing to check yet — write or translate some text first.'); return; }
    setBusy(true); setError(''); setResults(null);
    try {
      const r = await apiCall('/api/admin/copy-check', { method: 'POST', body: JSON.stringify({ context, texts: items }) });
      setResults(r.results);
    } catch (e) { setError(typeof e.message === 'string' ? e.message : 'AI check failed — please try again.'); } finally { setBusy(false); }
  };
  const markApplied = (key) => setResults((prev) => (prev || []).map((r) => (r.key === key ? { ...r, applied: true } : r)));
  const dismiss = (key) => setResults((prev) => (prev || []).filter((r) => r.key !== key));
  const clear = () => { setResults(null); setError(''); };
  return { results, busy, error, run, markApplied, dismiss, clear };
};

export const CopyCheckButton = ({ onClick, busy, size = 'sm', testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white text-slate-800 font-semibold hover:border-orange-400 hover:text-orange-700 transition-colors disabled:opacity-60 ${size === 'lg' ? 'px-4 py-2.5 text-sm' : 'px-2.5 py-1.5 text-xs'}`}
    data-testid={testId}
  >
    {busy ? <Loader2 className={`${size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} animate-spin`} /> : <ShieldCheck className={`${size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} text-orange-500`} />}
    {busy ? 'Checking…' : 'AI quality check'}
  </button>
);

const ResultRow = ({ r, label, onApply, onDismiss, testPrefix }) => {
  const issues = r.issues || [];
  const rewrite = r.rewrite;
  const key = r.key;
  const clean = !issues.length && !rewrite;
  const issueChips = issues.map((i, idx) => (
    <li key={idx} className={`text-[11px] border rounded px-1.5 py-0.5 ${TYPE_STYLE[i.type] || TYPE_STYLE.grammar}`} title={i.note} data-testid={`${testPrefix}-issue-${key}-${idx}`}>
      <span className="font-semibold">“{i.phrase}”</span>{i.note ? <span className="opacity-80"> — {i.note}</span> : null}
    </li>
  ));
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${clean ? 'bg-emerald-50/40 border-emerald-200' : 'bg-white border-slate-200'}`} data-testid={`${testPrefix}-row-${key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-bold text-slate-900">{label}</span>
        <span className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 ${scoreTone(r.score)}`} data-testid={`${testPrefix}-score-${key}`}>honesty {r.score}/100</span>
        {clean && <span className="text-[11px] text-emerald-700">reads plain and natural — nothing to fix</span>}
        <button type="button" onClick={() => onDismiss(key)} className="ml-auto text-slate-400 hover:text-slate-700" aria-label="Dismiss" data-testid={`${testPrefix}-dismiss-${key}`}><X className="w-3.5 h-3.5" /></button>
      </div>
      {issueChips.length > 0 && <ul className="flex flex-wrap gap-1.5">{issueChips}</ul>}
      {rewrite && (
        <div className="flex flex-col sm:flex-row sm:items-start gap-2">
          <p className="flex-1 text-xs text-slate-800 bg-slate-50 border border-dashed border-slate-300 rounded p-2 leading-relaxed" data-testid={`${testPrefix}-rewrite-${key}`}>{rewrite}</p>
          {r.applied ? (
            <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1 whitespace-nowrap" data-testid={`${testPrefix}-applied-${key}`}><Sparkles className="w-3 h-3" /> Applied — remember to save</span>
          ) : (
            <button type="button" onClick={() => onApply(r)} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-3 py-1.5 text-xs font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap" data-testid={`${testPrefix}-apply-${key}`}>
              <Wand2 className="w-3 h-3" /> Use plain version
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const CopyCheckPanel = ({ check, labelFor, onApply, testPrefix = 'copy-check' }) => {
  const { results, error, dismiss, markApplied, clear } = check;
  if (!results && !error) return null;
  const list = results || [];
  const flagged = list.filter((r) => r.issues.length || r.rewrite).length;
  const rows = list.map((r) => (
    <ResultRow key={r.key} r={r} label={labelFor(r)} onApply={(res) => { onApply(res); markApplied(res.key); }} onDismiss={dismiss} testPrefix={testPrefix} />
  ));
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid={testPrefix}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <ShieldCheck className="w-4 h-4 text-orange-500" />
        <span className="font-semibold text-slate-900" data-testid={`${testPrefix}-summary`}>
          {error ? error : flagged ? `${flagged} of ${list.length} texts could be plainer` : `All ${list.length} texts read plain and honest`}
        </span>
        <button type="button" onClick={clear} className="ml-auto text-slate-500 hover:underline" data-testid={`${testPrefix}-close`}>Close</button>
      </div>
      {rows}
    </div>
  );
};

export default CopyCheckPanel;
